import { auth } from "@lumberset/api-client";
import { initials } from "@lumberset/takeoff-ui";
import {
  Avatar,
  Badge,
  Button,
  Container,
  Field,
  Input,
  SectionHeader,
  Select,
  toast,
} from "@lumberset/ui";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { yardQuery } from "../../../lib/queries";

export const Route = createFileRoute("/_app/settings/team")({ component: Team });

function Team() {
  const queryClient = useQueryClient();
  const { data: yard } = useSuspenseQuery(yardQuery);
  const canInvite = yard.role !== "member";
  const [role, setRole] = useState<"member" | "admin">("member");

  const invite = useMutation({
    mutationFn: async (email: string) => {
      const result = await auth.organization.inviteMember({ email, role, organizationId: yard.id });
      if (result.error)
        throw new Error(result.error.message ?? "We could not send that invitation.");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["yard"] });
      toast.success("Invitation sent", { description: "It is good for 7 days." });
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    invite.mutate(String(new FormData(form).get("email")).trim(), {
      onSuccess: () => form.reset(),
    });
  }

  return (
    <>
      <Container className="divide-y p-0">
        <SectionHeader
          title="Members"
          description="Everyone at your yard can use LumberSet. There is no charge per person."
        />
        <ul className="divide-y divide-ui-border-base">
          {yard.members.map((member) => (
            <li key={member.id} className="flex items-center gap-x-3 px-6 py-3.5">
              <Avatar src={member.image} fallback={initials(member.name)} shape="circle" />
              <div className="min-w-0 flex-1">
                <div className="txt-compact-small-plus truncate text-ui-fg-base">{member.name}</div>
                <div className="txt-compact-xsmall truncate text-ui-fg-muted">{member.email}</div>
              </div>
              <Badge>{member.roleLabel}</Badge>
            </li>
          ))}
        </ul>
      </Container>

      {canInvite ? (
        <Container className="divide-y p-0">
          <SectionHeader title="Invite someone" />
          <form
            onSubmit={submit}
            className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-end"
          >
            <Field label="Work email" className="flex-1">
              {(control) => (
                <Input
                  {...control}
                  name="email"
                  type="email"
                  required
                  placeholder="name@youryard.com"
                />
              )}
            </Field>
            <Field label="Role" className="sm:w-56">
              {(control) => (
                <Select
                  value={role}
                  onValueChange={(value) => setRole(value as "member" | "admin")}
                >
                  <Select.Trigger id={control.id}>
                    <Select.Value />
                  </Select.Trigger>
                  <Select.Content>
                    <Select.Item value="member">Estimator</Select.Item>
                    <Select.Item value="admin">Manager</Select.Item>
                  </Select.Content>
                </Select>
              )}
            </Field>
            <Button type="submit" isLoading={invite.isPending}>
              Send invitation
            </Button>
          </form>
          <p className="txt-small px-6 py-3 text-ui-fg-subtle">
            Estimators run takeoffs and send quotes. Managers can also change the catalog,
            estimating standards and yard settings. Only the Owner manages billing.
          </p>
        </Container>
      ) : null}
    </>
  );
}
